/**
 * 应用外壳（DESIGN.md §7.1）。
 *
 * 只负责**布局骨架**：六个区域的位置与尺寸关系。各区域的实际内容
 * （工具状态机、图层树、属性表单）由后续任务填充，这里放的是占位内容。
 *
 * 术语跟随 Adobe 中文版惯例（§7.3）："图层"而非"层"，"不透明度"而非"透明度"。
 *
 * 分层约束：UI 层不得直接引用 TileStore 或 GPU backend（CLAUDE.md 规则 4）。
 * 本文件目前不 import 任何下层模块，画布区的实际渲染要等 M1-13 经应用层接入。
 */
import type { JSX } from 'solid-js';
import { For, createSignal } from 'solid-js';
import { AboutDialog } from '../about/AboutDialog';
import { Icon, type IconName } from '../icons/Icon';
import styles from './AppShell.module.css';

/** 菜单项。真正的下拉菜单在后续任务实现，这里只占位。 */
const MENUS = ['文件', '编辑', '图像', '图层', '选择', '滤镜', '视图', '窗口', '帮助'] as const;

/**
 * 工具栏条目——AppShell 独立挂载（无装配层注入真正的 Toolbar）时的兜底占位。
 * `key` 是快捷键（§7.4 跟随 Photoshop 惯例），`label` 用于无障碍名称。
 * `glyph` 是 `ui/icons/Icon.tsx` 的图标名，与 `interaction/tools/registry.ts`
 * 的同名工具保持一致（Lucide 线条图标，ISC 许可）。
 */
const TOOLS: ReadonlyArray<{ readonly id: string; readonly label: string; readonly key: string; readonly glyph: IconName }> = [
  { id: 'move', label: '移动', key: 'V', glyph: 'move' },
  { id: 'marquee', label: '选框', key: 'M', glyph: 'square-dashed' },
  { id: 'lasso', label: '套索', key: 'L', glyph: 'lasso' },
  { id: 'crop', label: '裁剪', key: 'C', glyph: 'crop' },
  { id: 'brush', label: '画笔', key: 'B', glyph: 'paintbrush' },
  { id: 'eraser', label: '橡皮擦', key: 'E', glyph: 'eraser' },
  { id: 'text', label: '文字', key: 'T', glyph: 'type' },
  { id: 'zoom', label: '缩放', key: 'Z', glyph: 'zoom-in' },
];

/**
 * 面板清单。`defaultVisible: false` 的面板默认隐藏，由「窗口」菜单开关
 * （PS 惯例：核心三件套常驻，动作/图层复合/脚本按需打开——六个面板全部
 * 常驻会把侧栏挤到每个面板只剩表头）。
 */
const PANELS = [
  { id: 'layers', title: '图层', placeholder: '尚无文档', defaultVisible: true },
  { id: 'properties', title: '属性 / 调整', placeholder: '未选择图层', defaultVisible: true },
  { id: 'history', title: '历史记录', placeholder: '尚无操作', defaultVisible: true },
  { id: 'actions', title: '动作', placeholder: '还没有录制或导入过动作', defaultVisible: false },
  { id: 'layerComps', title: '图层复合', placeholder: '还没有图层复合', defaultVisible: false },
  { id: 'script', title: '脚本', placeholder: '', defaultVisible: false },
] as const;

export type PanelId = (typeof PANELS)[number]['id'];

export interface AppShellProps {
  /** 当前激活的工具 id。受控——工具状态机在交互层，不归 UI 层管。 */
  readonly activeTool?: string;
  /** 状态栏右侧的自由文本，通常是当前操作提示。 */
  readonly hint?: string;
  /**
   * 菜单栏内容（M2-02 起由装配层传入 MenuBar）。
   * 缺省时回退到静态占位按钮——外壳单独挂载时仍然完整。
   */
  readonly menubar?: JSX.Element;
  /** 状态栏的文档信息（标题 · 尺寸）。缺省显示「尚未打开文档」。 */
  readonly statusDocument?: string | undefined;
  /** 状态栏缩放显示。 */
  readonly zoom?: string;
  /** 左工具栏内容（M2-05 起由装配层传入 Toolbar）。缺省回退静态占位。 */
  readonly toolbar?: JSX.Element;
  /** 工具属性栏内容。缺省显示激活工具名或占位文案。 */
  readonly optionsbar?: JSX.Element;
  /** 画布区内容。缺省时显示空态。 */
  readonly children?: JSX.Element;
  /** 外部受控的关于面板开关（菜单动作 help.about 用）。 */
  readonly aboutOpen?: boolean;
  readonly onAboutClose?: () => void;
  /** 右侧面板内容（M3-02 起由装配层注入）。缺省显示占位文案。 */
  readonly panelSlots?: Partial<Record<PanelId, JSX.Element>>;
  /**
   * 面板显隐覆盖（「窗口」菜单的开关状态）。缺省项按 PANELS 的
   * defaultVisible 取值——AppShell 独立挂载时无需装配层也有合理默认。
   */
  readonly panelVisibility?: Partial<Record<PanelId, boolean>>;
}

export function AppShell(props: AppShellProps): JSX.Element {
  const [aboutOpen, setAboutOpen] = createSignal(false);

  return (
    <div class={styles.shell} data-testid="app-shell">
      <header class={styles.menubar} data-testid="menubar">
        {props.menubar ?? (
          <For each={MENUS}>
            {(menu) => (
              <button
                type="button"
                class={styles.menuButton}
                // 占位回退：「帮助」直接打开关于面板（§13.1 的合规要求）。
                // 正式菜单由 MenuBar（M2-02）经 menubar prop 传入。
                onClick={menu === '帮助' ? () => setAboutOpen(true) : undefined}
                data-testid={menu === '帮助' ? 'menu-help' : undefined}
              >
                {menu}
              </button>
            )}
          </For>
        )}
      </header>

      {/* data-keep-text-session：文字工具编辑中点这一整栏（字体/字号/颜色
          色块等）都会让浮动 <textarea> 失焦——`text-tool.ts` 靠这个属性
          识别"焦点只是挪来这里，不是真的要放弃编辑"，不该借机提交
          （M4-38：拖选一段文字改字体/颜色） */}
      <div
        class={styles.optionsbar}
        data-testid="optionsbar"
        role="toolbar"
        aria-label="工具属性"
        data-keep-text-session
      >
        {props.optionsbar ?? (
          <span>{props.activeTool === undefined ? '未选择工具' : `工具：${props.activeTool}`}</span>
        )}
      </div>

      <nav class={styles.toolbar} data-testid="toolbar" aria-label="工具栏">
        {props.toolbar ?? (
          <For each={TOOLS}>
            {(tool) => (
              <button
                type="button"
                class={styles.toolButton}
                title={`${tool.label} (${tool.key})`}
                aria-label={tool.label}
                aria-pressed={props.activeTool === tool.id}
              >
                <Icon name={tool.glyph} />
              </button>
            )}
          </For>
        )}
      </nav>

      <main class={styles.canvas} data-testid="canvas-area" aria-label="画布工作区">
        {props.children}
      </main>

      <aside class={styles.panels} data-testid="panels" aria-label="面板">
        <For each={PANELS.filter((p) => props.panelVisibility?.[p.id] ?? p.defaultVisible)}>
          {(panel) => (
            <section
              class={styles.panel}
              classList={{ [styles.panelDominant!]: panel.id === 'layers' }}
              data-testid={`panel-${panel.id}`}
            >
              <h2 class={styles.panelHeader}>{panel.title}</h2>
              <div class={styles.panelBody}>
                {props.panelSlots?.[panel.id] ?? panel.placeholder}
              </div>
            </section>
          )}
        </For>
      </aside>

      <footer class={styles.statusbar} data-testid="statusbar">
        <span class={styles.statusItem}>{props.zoom ?? '100%'}</span>
        <span class={styles.statusItem} data-testid="status-document">
          {props.statusDocument ?? '尚未打开文档'}
        </span>
        <span class={styles.statusItem}>内存 0 MB</span>
        <span class={styles.spacer} />
        <span>{props.hint ?? ''}</span>
      </footer>

      <AboutDialog
        open={aboutOpen() || props.aboutOpen === true}
        onClose={() => {
          setAboutOpen(false);
          props.onAboutClose?.();
        }}
      />
    </div>
  );
}
